import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, BookOpen, Globe, Building2, AlertTriangle, TrendingDown,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import KBQuizModal from './KBQuizModal';
import { API } from '../../components/kb/utils';
import { KBCard } from '../../components/kb/admin/KBCard';
import { CreateKBForm } from '../../components/kb/admin/CreateKBForm';

const AdminKnowledgeBasesPage = () => {
  const { token } = useAuth();
  const [domains, setDomains] = useState([]);
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [kbs, setKbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('');
  const [quizKB, setQuizKB] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Load domains list
  useEffect(() => {
    axios.get(`${API}/admin/domains`, { headers })
      .then(r => {
        setDomains(r.data || []);
        if (r.data?.length > 0) setSelectedDomainId(r.data[0].id);
      })
      .catch(() => {});
    // headers is derived from token; loading domains once on token change is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadKBs = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedDomainId ? { domain_id: selectedDomainId } : {};
      const r = await axios.get(`${API}/knowledge-bases`, { headers, params });
      setKbs(r.data || []);
    } catch (err) {
      toast.error('Failed to load knowledge bases');
      console.error('Load KBs error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDomainId, headers]);

  useEffect(() => { loadKBs(); }, [loadKBs]);

  // ── Escalation-ratio suggestions ─────────────────────────────────────────
  const [suggestions, setSuggestions] = useState([]);
  const [applyingSuggestion, setApplyingSuggestion] = useState(null);

  const loadSuggestions = useCallback(async () => {
    try {
      const params = selectedDomainId ? `?domain_id=${selectedDomainId}` : '';
      const r = await axios.get(`${API}/admin/kb-escalation-suggestions${params}`, { headers, timeout: 30000 });
      setSuggestions(r.data?.suggestions || []);
    } catch (err) {
      console.debug('Escalation suggestions unavailable', err?.response?.status || err?.message);
      setSuggestions([]);
    }
  }, [selectedDomainId, headers]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions, kbs]);

  const applySuggestion = async (s) => {
    if (s.action !== 'lower_threshold') {
      toast.info(`Open "${s.kb_name}" and click "AI Suggest" in the Sub-Intents section to add a lenient subtype.`);
      return;
    }
    if (!window.confirm(
      `Lower threshold on "${s.kb_name}" from ${s.current_threshold} → ${s.suggested_threshold}?\n\nThis affects grading of all future calls on this KB.`,
    )) return;
    setApplyingSuggestion(s.kb_id);
    try {
      await axios.put(
        `${API}/knowledge-bases/${s.kb_id}`,
        { threshold_score: s.suggested_threshold },
        { headers, timeout: 30000 },
      );
      toast.success(`Threshold lowered to ${s.suggested_threshold} on "${s.kb_name}"`);
      await loadKBs();
      await loadSuggestions();
    } catch (err) {
      console.error('Apply suggestion failed', err?.response || err);
      toast.error(err?.response?.data?.detail || 'Failed to apply suggestion');
    } finally {
      setApplyingSuggestion(null);
    }
  };

  // When a domain is selected, only show KBs for that domain (not global)
  const displayKBs = selectedDomainId
    ? kbs.filter(kb => kb.domain_id === selectedDomainId)
    : kbs;

  const filtered = filter
    ? displayKBs.filter(kb =>
        kb.name.toLowerCase().includes(filter.toLowerCase())
        || kb.call_type.toLowerCase().includes(filter.toLowerCase()),
      )
    : displayKBs;

  const sortedGrouped = useMemo(() => {
    const grouped = filtered.reduce((acc, kb) => {
      const key = kb.call_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      acc[key] = [...(acc[key] || []), kb];
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const selectedDomain = domains.find(d => d.id === selectedDomainId);
  const domainKBCount = displayKBs.filter(kb => kb.domain_id === selectedDomainId).length;

  const handleQuiz = (kb) => {
    if (!selectedDomainId) { toast.error('Select a domain first'); return; }
    setQuizKB(kb);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Knowledge Bases</h1>
            <p className="text-white/40 text-sm mt-0.5">
              {domainKBCount} knowledge bases · {displayKBs.reduce((s, k) => s + (k.criteria?.length || 0), 0)} total criteria
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-sm font-medium rounded-lg transition-colors"
              data-testid="add-kb-btn"
            >
              <Plus className="w-4 h-4" />New KB
            </button>
          </div>
        </div>

        {/* Escalation-ratio suggestions banner */}
        {suggestions.length > 0 && (
          <div
            className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4"
            data-testid="kb-escalation-suggestions"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h2 className="text-amber-200 font-semibold text-sm">
                Grading may be too strict on {suggestions.length} knowledge base{suggestions.length === 1 ? '' : 's'}
              </h2>
            </div>
            <div className="space-y-2">
              {suggestions.slice(0, 5).map(s => (
                <div
                  key={s.kb_id}
                  className="flex items-start justify-between gap-3 bg-black/20 border border-amber-500/20 rounded-lg p-3"
                  data-testid={`kb-suggestion-${s.kb_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 text-sm font-medium">
                      {s.kb_name}
                      {s.domain_name && <span className="text-white/40 font-normal"> · {s.domain_name}</span>}
                    </p>
                    <p className="text-white/60 text-xs mt-1">{s.message}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/40">
                      <span className="flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" />
                        {Math.round(s.escalated_ratio * 100)}% escalation rate
                      </span>
                      <span>{s.escalated}/{s.total} calls</span>
                    </div>
                  </div>
                  <button
                    onClick={() => applySuggestion(s)}
                    disabled={applyingSuggestion === s.kb_id}
                    className="shrink-0 px-3 py-1.5 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 rounded-lg font-medium transition-colors disabled:opacity-50"
                    data-testid={`kb-suggestion-apply-${s.kb_id}`}
                  >
                    {applyingSuggestion === s.kb_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : s.action === 'lower_threshold'
                        ? `Lower to ${s.suggested_threshold}`
                        : 'Open KB'}
                  </button>
                </div>
              ))}
              {suggestions.length > 5 && (
                <p className="text-white/30 text-xs pt-1">+ {suggestions.length - 5} more suggestions hidden</p>
              )}
            </div>
          </div>
        )}

        {/* Domain selector */}
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-white/40 text-xs font-medium uppercase tracking-wide">Domain</p>
          <div className="flex flex-wrap gap-2">
            {domains.map(d => (
              <button
                key={d.id}
                onClick={() => setSelectedDomainId(d.id)}
                data-testid={`domain-tab-${d.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  selectedDomainId === d.id
                    ? 'bg-teal-500/20 border border-teal-500/40 text-teal-300'
                    : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/70'
                }`}
              >
                <Building2 className="w-3 h-3" />
                {d.company?.name || d.yabbit_domain}
              </button>
            ))}
            <button
              onClick={() => setSelectedDomainId('')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                !selectedDomainId
                  ? 'bg-amber-500/20 border border-amber-500/30 text-amber-300'
                  : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/70'
              }`}
            >
              <Globe className="w-3 h-3" />Global Templates
            </button>
          </div>
        </div>

        {/* Domain info banner */}
        {selectedDomain && (
          <div className="flex items-center gap-3 bg-white/[0.02] border border-white/5 rounded-xl px-4 py-3">
            <Building2 className="w-4 h-4 text-teal-400/60" />
            <div>
              <span className="text-white/70 text-sm font-medium">{selectedDomain.company?.name || selectedDomain.yabbit_domain}</span>
              <span className="text-white/30 text-xs ml-2">({selectedDomain.yabbit_domain})</span>
            </div>
            <span className="text-white/20 text-xs ml-auto">Industry: {selectedDomain.industry?.replace(/_/g, ' ')}</span>
          </div>
        )}

        {/* Search */}
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search by name or call type..."
          className="w-full max-w-sm bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
        />

        {/* Create form */}
        {creating && (
          <CreateKBForm
            token={token}
            domainId={selectedDomainId || null}
            onCreated={() => { setCreating(false); loadKBs(); }}
            onCancel={() => setCreating(false)}
          />
        )}

        {/* KB List grouped by call_type */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-white/20 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/20">
            <BookOpen className="w-10 h-10 mx-auto mb-3" />
            <p className="text-sm">
              {selectedDomainId ? 'No domain-specific knowledge bases found' : 'No knowledge bases found'}
            </p>
            {selectedDomainId && (
              <p className="text-xs mt-2 text-white/15">
                Use &quot;New KB&quot; or &quot;AI Coach&quot; to build knowledge bases for this domain
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {sortedGrouped.map(([type, items]) => (
              <div key={type}>
                <h2 className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-2 px-1">{type}</h2>
                <div className="space-y-2">
                  {items.map(kb => (
                    <KBCard
                      key={kb.id}
                      kb={kb}
                      token={token}
                      onUpdated={loadKBs}
                      onQuiz={handleQuiz}
                      selectedDomainId={selectedDomainId}
                      domains={domains}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {quizKB && (
        <KBQuizModal
          kb={quizKB}
          domainId={selectedDomainId}
          onClose={() => setQuizKB(null)}
          onKBUpdated={loadKBs}
        />
      )}
    </AdminLayout>
  );
};

export default AdminKnowledgeBasesPage;
